import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { MUTATION_TYPE, useAssessment, useAuth, useToast } from "hooks";
import { QuestionsList } from "components/Assessment";
import { Header } from "components/DisplayBox";
import { MODAL_SIZE, ModalForm } from "components/Modal";
import { SIZE, TYPE, TYPE_KEYNAMES } from "components/Form";
import {
  ASSESSMENT_CONFIGS,
  FILE_TYPE_OPTIONS,
  FILE_UPLOAD_DEFAULTS,
  FILE_UPLOAD_LIMITS,
  QUESTION_SETTINGS,
  QUESTION_TYPE_CONFIGS,
  QUESTION_TYPE_OPTIONS,
  fileTypeOptions,
} from "constants/index";
import { GET_ASSESSMENT } from "graphql/queries";
import { ADD_MISSION_ROLE_ASSESSMENT_QUESTION, UPDATE_MISSION_ROLE_ASSESSMENT_QUESTION } from "graphql/mutations";
import { H3 } from "components/Text";
import { EditLink } from "components/Links";
import { EditButton } from "components/Buttons";
import { MessagesNoResults } from "components/Messages";

/**
 * RoleQuestionnaire
 *
 * @param {Object}     missionRole
 * @param {Function}   refetch
 * @param {Boolean}    loading
 * @param {Boolean}    canEdit        Defaults to admin-only; pass it where another role may manage the vetting
 */
const RoleQuestionnaire = ({ missionRole, refetch, loading: loadingProp, canEdit: canManage, ...props }) => {
  const { toast } = useToast();
  const intl = useIntl();
  const { isAdmin } = useAuth();
  const canManageQuestionnaire = canManage ?? isAdmin;
  const [showForm, setShowForm] = useState(false);
  const { assessmentId, requirementsLocked: isLocked } = missionRole || {};
  const [selectedQuestion, setSelectedQuestion] = useState({});
  const [formValues, setFormValues] = useState({});
  const { data, loading, getRowOptions, movingQuestion, handleToggleQuestion, updatingQuestion } = useAssessment({
    id: assessmentId,
    skipAnswers: true,
  });
  const questionsExists = !!data?.questions?.length;
  const isEdit = selectedQuestion?.id;
  const canEdit = canManageQuestionnaire && !isLocked;

  const handleClose = () => {
    setShowForm(false);
    setSelectedQuestion({});
    setFormValues({});
  };

  const handleEdit = (question) => {
    setSelectedQuestion(question);
    setShowForm(true);
  };

  const handleComplete = () => {
    if (!assessmentId) {
      refetch();
    }
    handleClose();
    toast.success(<FormattedMessage id={`RoleQuestionnaire.AddQuestionForm.${isEdit ? "Update" : "Create"}Success`} />);
  };

  const handleError = () => {
    toast.error(<FormattedMessage id={`RoleQuestionnaire.AddQuestionForm.${isEdit ? "Update" : "Create"}Error`} />);
  };

  const initialValues = {
    ...(isEdit
      ? {
          answerType: selectedQuestion?.type,
          wording: selectedQuestion?.wording,
          options: selectedQuestion?.options?.map(({ value }) => ({ value })) || [],
          properties: {
            charactersLimit: selectedQuestion?.properties?.charactersLimit?.toString(),
            required: selectedQuestion?.properties?.required,
            acceptedTypes: fileTypeOptions(selectedQuestion?.properties?.acceptedTypes),
            maxFileSize: selectedQuestion?.properties?.maxFileSize?.toString(),
            maxFiles: selectedQuestion?.properties?.maxFiles?.toString(),
          },
          assessmentId,
        }
      : {
          ...ASSESSMENT_CONFIGS.missionRole,
          answerType: TYPE_KEYNAMES.textarea,
          assessmentableId: missionRole?.id,
          properties: {
            charactersLimit: "100",
            required: true,
            acceptedTypes: fileTypeOptions(FILE_UPLOAD_DEFAULTS.acceptedTypes),
            maxFileSize: FILE_UPLOAD_DEFAULTS.maxFileSize,
            maxFiles: FILE_UPLOAD_DEFAULTS.maxFiles,
          },
        }),
  };

  const answerType = formValues?.answerType || initialValues.answerType;
  const formData = useMemo(() => FORM_DATA({ answerType, isEdit, intl }), [answerType, isEdit, intl]);

  const formProps = {
    initialValues,
    mutationType: isEdit ? MUTATION_TYPE.update : MUTATION_TYPE.add,
    updateId: selectedQuestion?.id,
    mutationData: {
      mutationName: {
        add: ADD_MISSION_ROLE_ASSESSMENT_QUESTION,
        update: UPDATE_MISSION_ROLE_ASSESSMENT_QUESTION,
      },
      refetchAfterMutate: true,
      refetchQueries: [GET_ASSESSMENT],
    },
  };

  const rowOptions = (question) => getRowOptions({ question, assessment: data, handleEdit });

  return (
    <>
      <Header {...props}>
        <H3>
          <FormattedMessage id="Missions.MissionRole.Questionnaire" />
        </H3>
        {questionsExists && canEdit && (
          <EditLink onClick={() => setShowForm(true)}>
            <FormattedMessage id="RoleQuestionnaire.EditButtonText" />
          </EditLink>
        )}
      </Header>
      {questionsExists || loadingProp || loading || updatingQuestion ? (
        <QuestionsList
          loading={loading || loadingProp || updatingQuestion || movingQuestion}
          data={data?.questions || []}
          disablePagination
          rowOptions={canManageQuestionnaire ? rowOptions : null}
          onToggleQuestion={handleToggleQuestion}
          assessmentId={assessmentId}
          canEdit={canEdit}
        />
      ) : canEdit ? (
        <EditButton onClick={() => setShowForm(true)}>
          <FormattedMessage id="RoleQuestionnaire.EditButtonText" />
        </EditButton>
      ) : (
        <MessagesNoResults title="RoleQuestionnaire.EmptyMsg" />
      )}
      {showForm && (
        <ModalForm
          data={formData}
          show={showForm}
          onChange={setFormValues}
          onClose={handleClose}
          onCompleted={handleComplete}
          onError={handleError}
          size={MODAL_SIZE.medium}
          numColumns={3}
          title={<FormattedMessage id={`RoleCodeChallenges.${isEdit ? "Edit" : "Add"}QuestionForm.Title`} />}
          {...formProps}
        />
      )}
    </>
  );
};

const SETTINGS_FIELDS = (intl) => ({
  [QUESTION_SETTINGS.charactersLimit]: {
    label: "Chars Limit",
    type: TYPE.input,
    size: SIZE.small,
    parentObjectKey: "properties",
    properties: {
      type: "number",
      name: QUESTION_SETTINGS.charactersLimit,
    },
  },
  [QUESTION_SETTINGS.options]: {
    label: <FormattedMessage id="RoleQuestionnaire.AddQuestionForm.Options" />,
    type: TYPE.tags,
    size: SIZE.xlarge,
    properties: {
      name: QUESTION_SETTINGS.options,
      labelKey: "value",
      valueKey: "value",
      allowNew: true,
      minLength: 1,
      placeholder: intl.formatMessage({ id: "RoleQuestionnaire.AddQuestionForm.Options.Placeholder" }),
    },
  },
  [QUESTION_SETTINGS.maxFileSize]: {
    label: <FormattedMessage id="RoleQuestionnaire.AddQuestionForm.MaxFileSize" />,
    type: TYPE.input,
    size: SIZE.small,
    parentObjectKey: "properties",
    properties: {
      type: "number",
      name: QUESTION_SETTINGS.maxFileSize,
      min: 1,
      max: FILE_UPLOAD_LIMITS.maxFileSize,
    },
  },
  [QUESTION_SETTINGS.maxFiles]: {
    label: <FormattedMessage id="RoleQuestionnaire.AddQuestionForm.MaxFiles" />,
    type: TYPE.input,
    size: SIZE.small,
    parentObjectKey: "properties",
    properties: {
      type: "number",
      name: QUESTION_SETTINGS.maxFiles,
      min: 1,
      max: FILE_UPLOAD_LIMITS.maxFiles,
    },
  },
  [QUESTION_SETTINGS.acceptedTypes]: {
    label: <FormattedMessage id="RoleQuestionnaire.AddQuestionForm.AcceptedTypes" />,
    type: TYPE.tags,
    size: SIZE.xlarge,
    parentObjectKey: "properties",
    properties: {
      name: QUESTION_SETTINGS.acceptedTypes,
      options: FILE_TYPE_OPTIONS,
      labelKey: "label",
      valueKey: "value",
      minLength: 0,
      placeholder: intl.formatMessage({ id: "RoleQuestionnaire.AddQuestionForm.AcceptedTypes.Placeholder" }),
    },
  },
});

const settingFields = (enabledSettings, intl) =>
  Object.entries(SETTINGS_FIELDS(intl)).map(([key, field]) => {
    const enabled = enabledSettings.includes(key);

    return {
      ...field,
      hideField: !enabled,
      disabled: !enabled,
      properties: { ...field.properties, required: enabled },
    };
  });

const FORM_DATA = ({ answerType, isEdit, intl }) => [
  {
    items: [
      {
        label: "Question Text",
        type: TYPE.input,
        size: SIZE.xlarge,
        properties: {
          name: "wording",
          required: true,
        },
      },
      {
        label: "Type",
        type: TYPE.select,
        size: SIZE.small,
        disabled: isEdit,
        properties: {
          options: QUESTION_TYPE_OPTIONS,
          name: "answerType",
          required: true,
        },
      },
      {
        label: "Required",
        type: TYPE.select,
        size: SIZE.small,
        parentObjectKey: "properties",
        properties: {
          name: "required",
          required: true,
          options: [
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ],
        },
      },
      ...settingFields(QUESTION_TYPE_CONFIGS[answerType]?.settings || [], intl),
    ],
  },
];

RoleQuestionnaire.defaultProps = {
  refetch: () => {},
};

RoleQuestionnaire.propTypes = {
  missionRole: PropTypes.object,
  refetch: PropTypes.func,
  loading: PropTypes.bool,
  canEdit: PropTypes.bool,
};

export default RoleQuestionnaire;
